// ============= Imports =============
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Input,
    makeStyles,
    Text,
    Toast,
    ToastBody,
} from '@fluentui/react-components';
import { Add20Regular, DocumentArrowUp20Regular, Subtract20Regular, Warning12Filled } from '@fluentui/react-icons';
import * as React from 'react';
import { useState } from 'react';
import { ChatMemorySource } from '../../../libs/models/ChatMemorySource';
import { useAppDispatch } from '../../../redux/app/hooks';
import { addAlert } from '../../../redux/features/app/appSlice';
import { AlertType } from '../../../libs/models/AlertType';
import { validateUrl } from './validateURL';

// ============= Styles =============
const useStyles = makeStyles({
    content: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '10px',
    },
    errorText: {
        color: '#d13438',
        fontSize: '12px',
        marginTop: '4px',
    },
    loadingText: {
        fontSize: '20px',
        marginTop: '1rem',
        marginBottom: '1rem',
    },
    inputWrapper: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '4px',
    },
    inputAndButtons: {
        display: 'flex',
        flexDirection: 'row',
        columnGap: '5px',
        alignItems: 'center',
    },
    inputsContainer: {
        width: '100%',
    },
    warningIcon: {
        width: '24px',
    },
    customToast: {
        backgroundColor: 'khaki',
        marginBottom: '14px',
    },
});

// ============= Types =============
interface UrlInputDialogProps {
    isDisabled: boolean;
    onUrlSubmit: (urls: string[]) => Promise<void>;
    resources?: ChatMemorySource[];
    allowMultipleUrls?: boolean;
}

// ============= Component =============
export const UrlInputDialog: React.FC<UrlInputDialogProps> = ({
    isDisabled,
    onUrlSubmit,
    resources,
    allowMultipleUrls = false,
}) => {
    const styles = useStyles();
    const [urls, setUrls] = useState<string[]>(['']);
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Array<string | null>>([null]);
    const [dots, setDots] = useState('');
    const [showDelayedMessage, setShowDelayedMessage] = useState(false);
    const dispatch = useAppDispatch();

    // Loading dots animation effect
    React.useEffect(() => {
        if (isSubmitting) {
            const interval = setInterval(() => {
                setDots((prev) => (prev.length < 3 ? prev + '.' : ''));
            }, 500);
            return () => {
                clearInterval(interval);
            };
        }
        return undefined;
    }, [isSubmitting]);

    // Delayed message effect
    React.useEffect(() => {
        if (isSubmitting) {
            const timeout = setTimeout(() => {
                setShowDelayedMessage(true);
            }, 20000);

            return () => {
                clearTimeout(timeout);
                setShowDelayedMessage(false);
            };
        }
        return undefined;
    }, [isSubmitting]);

    const validateUrls = (
        urls: string[],
        resources?: ChatMemorySource[],
    ): { isValid: boolean; errors: Array<string | null> } => {
        const validationResults = urls.map((url) => validateUrl(url, () => {}, resources));
        const errors = validationResults.map((result) => result.error);
        setValidationErrors(errors);

        return {
            isValid: validationResults.every((result) => result.isValid),
            errors,
        };
    };

    const handleAddUrl = () => {
        if (allowMultipleUrls) {
            setUrls([...urls, '']);
            setValidationErrors([...validationErrors, null]);
        }
    };

    const handleRemoveUrl = (index: number) => {
        if (allowMultipleUrls && urls.length > 1) {
            const newUrls = urls.filter((_, i) => i !== index);
            const newErrors = validationErrors.filter((_, i) => i !== index);
            setUrls(newUrls);
            setValidationErrors(newErrors);
        }
    };

    const handleUrlChange = (index: number, value: string) => {
        const newUrls = [...urls];
        newUrls[index] = value;
        setUrls(newUrls);

        if (validationErrors[index]) {
            const newErrors = [...validationErrors];
            newErrors[index] = null;
            setValidationErrors(newErrors);
        }
    };

    const handleSubmit = async () => {
        const { isValid } = validateUrls(urls, resources);
        if (isValid) {
            setIsSubmitting(true);
            try {
                const standardizedUrls = await Promise.all(
                    urls.map((url) => {
                        try {
                            const urlObj = new URL(url.includes('://') ? url : `https://${url}`);
                            if (!urlObj.hostname.startsWith('www.')) {
                                const protocol = urlObj.protocol;
                                const path = urlObj.pathname + urlObj.search + urlObj.hash;
                                return `${protocol}//www.${urlObj.hostname}${path}`;
                            }
                            return url;
                        } catch (error) {
                            console.error('Error standardizing URL:', error);
                            return url;
                        }
                    }),
                );

                await onUrlSubmit(standardizedUrls);
                setUrls(['']);
                setValidationErrors([null]);
                setIsOpen(false);
            } catch (error) {
                dispatch(
                    addAlert({
                        message: error instanceof Error ? error.message : String(error),
                        type: AlertType.Error,
                    }),
                );
                console.error('Error submitting URLs:', error);
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleOpenChange = (_e: any, data: { open: boolean }) => {
        setIsOpen(data.open);
        if (!data.open) {
            setValidationErrors([]);
            setUrls(['']);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger disableButtonEnhancement>
                <Button icon={<DocumentArrowUp20Regular />} disabled={isDisabled}>
                    Embed URLs
                </Button>
            </DialogTrigger>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>Embed URLs</DialogTitle>
                    <DialogContent className={styles.content}>
                        <Text italic>
                            Please note that any URL embedded will convert the web page into a document for use by
                            Litium. The information in the web page will not update as the underlying web page updates.
                            URLs can be embedded again to capture any changes in the web page which have been made
                            subsequently to the previous URL upload.
                        </Text>
                        <Text>
                            Enter the URL{allowMultipleUrls ? 's' : ''}{' '}you want to embed as a pdf:
                        </Text>
                        {isSubmitting && showDelayedMessage && (
                            <Toast className={styles.customToast}>
                                <ToastBody>
                                    <Warning12Filled className={styles.warningIcon} />
                                    Data is still embedding but may be slower due to the size of the webpage.
                                </ToastBody>
                            </Toast>
                        )}
                        {urls.map((url, index) => (
                            <div key={index} className={styles.inputWrapper}>
                                <div className={styles.inputAndButtons}>
                                    <Input
                                        className={styles.inputsContainer}
                                        type="url"
                                        value={url}
                                        onChange={(_e, data) => {
                                            handleUrlChange(index, data.value);
                                        }}
                                        disabled={isSubmitting}
                                    />
                                    {allowMultipleUrls && (
                                        <>
                                            {urls.length > 1 && (
                                                <Button
                                                    icon={<Subtract20Regular />}
                                                    appearance="subtle"
                                                    onClick={() => {
                                                        handleRemoveUrl(index);
                                                    }}
                                                    disabled={isSubmitting}
                                                />
                                            )}
                                            {index === urls.length - 1 && (
                                                <Button
                                                    icon={<Add20Regular />}
                                                    appearance="subtle"
                                                    onClick={handleAddUrl}
                                                    disabled={isSubmitting}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                                {validationErrors[index] && (
                                    <Text className={styles.errorText}>{validationErrors[index]}</Text>
                                )}
                            </div>
                        ))}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            appearance="secondary"
                            onClick={() => {
                                setIsOpen(false);
                                setUrls(['']);
                            }}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            appearance="primary"
                            onClick={() => void handleSubmit()}
                            disabled={urls.some((url) => !url.trim()) || isSubmitting}
                        >
                            {isSubmitting ? `Embedding${dots}` : 'Embed'}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
